// 권한 그룹 체크박스 - permissionGroup별 헤더 + 개별 체크

import type { PermissionGroupBucket } from "../utils/groupPermissions";
import { Checkbox } from "@/components/ui/checkbox";

interface PermissionCheckboxGroupProps {
  groups: PermissionGroupBucket[];
  selectedGuids: Set<string>;
  onToggle: (permissionGuid: string, checked: boolean) => void;
  /** 역할 상속 등 비활성 표시 (체크된 채로 disabled) */
  inheritedGuids?: Set<string>;
  disabled?: boolean;
}

export function PermissionCheckboxGroup({
  groups,
  selectedGuids,
  onToggle,
  inheritedGuids,
  disabled = false,
}: PermissionCheckboxGroupProps) {
  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <GroupSection
          key={g.groupCode}
          group={g}
          selectedGuids={selectedGuids}
          onToggle={onToggle}
          inheritedGuids={inheritedGuids}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

interface GroupSectionProps {
  group: PermissionGroupBucket;
  selectedGuids: Set<string>;
  onToggle: (permissionGuid: string, checked: boolean) => void;
  inheritedGuids?: Set<string>;
  disabled: boolean;
}

function GroupSection({
  group,
  selectedGuids,
  onToggle,
  inheritedGuids,
  disabled,
}: GroupSectionProps) {
  const total = group.permissions.length;
  const checkedCount = group.permissions.reduce(
    (acc, p) => (selectedGuids.has(p.guid) ? acc + 1 : acc),
    0,
  );

  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-800">
            {group.groupLabel}
          </span>
          <span className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
            {checkedCount}/{total}
          </span>
        </div>
      </div>
      <div className="p-2">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          {group.permissions.map((p) => {
            const isChecked = selectedGuids.has(p.guid);
            const isInherited = inheritedGuids?.has(p.guid) ?? false;
            const rowDisabled = disabled || isInherited;
            return (
              <label
                key={p.guid}
                className={`flex items-start gap-2 rounded px-2 py-1.5 text-xs ${
                  rowDisabled
                    ? "cursor-not-allowed opacity-70"
                    : "cursor-pointer hover:bg-gray-50"
                }`}
              >
                <Checkbox
                  checked={isChecked || isInherited}
                  onCheckedChange={(next) => {
                    if (rowDisabled) return;
                    onToggle(p.guid, next === true);
                  }}
                  disabled={rowDisabled}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium text-gray-900">{p.label}</span>
                    <span className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[10px] text-gray-500">
                      {p.code}
                    </span>
                    {isInherited && (
                      <span className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                        역할 상속
                      </span>
                    )}
                  </div>
                  {p.description && (
                    <div className="mt-0.5 line-clamp-1 text-[10px] text-gray-500">
                      {p.description}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
